import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { Product } from '@/types';
import { api } from '@/api/client';
import { useAppContext } from '@/hooks';
import { Badge, Button, Card, EmptyState, Spinner } from '@/components/ui';
import { ProductArtwork } from '@/components/products/ProductArtwork';
import { AssistLauncherHost, useAssistLauncher } from '@/components/shared/AssistLauncher';

const statusLabels: Record<string, string> = {
  AVAILABLE: 'products.available',
  SETUP_REQUIRED: 'products.setupRequired',
  UNAVAILABLE: 'products.unavailable',
  NOT_SUBSCRIBED: 'products.notSubscribed',
  ACCESS_PENDING: 'products.accessPending',
  PERMISSION_REQUIRED: 'products.permissionRequired',
};

export function ProductsPage() {
  const { t } = useTranslation();
  const { activeContext } = useAppContext();
  const institutionId = activeContext?.institution?.id;
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const { available: assistAvailable, open: assistOpen, openAssist, closeAssist } = useAssistLauncher();

  useEffect(() => {
    if (!institutionId) return;

    // The INSTITUTION'S products, via the MEMBER route.
    //
    // This used to call `/products` (the whole sales catalogue), then the
    // administrator route at /admin/institution/:id/products — which 403'd for
    // every non-administrator (an Admissions Officer, a Teacher, a Parent), and
    // the catch below turned that 403 into an empty list. "No products
    // available" and "you were not allowed to ask" are different answers, and
    // showing the first for the second sent people to check subscriptions that
    // were fine all along. The member route answers for any role held at the
    // institution.
    setLoading(true);
    setFailed(false);
    api.products
      .forInstitution(institutionId)
      // Keep an unexpected BFF envelope or malformed response from reaching the render
      // path. The API client normalizes known envelopes too; this boundary is deliberately
      // defensive for partial deployments.
      .then((result) => setProducts(Array.isArray(result) ? result : []))
      // A failure is a failure, never an empty state.
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [institutionId]);

  // Only what this school actually has. NOT_SUBSCRIBED is the rest of the catalogue, and
  // listing it turns a workspace into a price list — an administrator opening this page
  // wants the things they can use, not the things they could buy.
  //
  // The user is ALREADY inside CampusOne Core when they reach this page, so the product
  // that hosts it is hidden — launching yourself from a product you are standing in reads
  // as a broken button, and an administrator who needs to manage the subscription can do
  // so from the configuration surface.
  const entitled = products.filter(
    (product) => product.status !== 'NOT_SUBSCRIBED' && product.code !== 'CAMPUSONE_CORE',
  );

  if (loading) {
    return <div className="page-loading"><Spinner label={t('common.loading')} /></div>;
  }

  if (failed) {
    return (
      <div className="page-products">
        <div className="page-header">
          <h1>{t('products.title')}</h1>
          <p className="page-subtitle">{t('products.subtitle')}</p>
        </div>
        <EmptyState
          icon={<Icon name="error" size={48} />}
          title={t('products.loadFailedTitle')}
          description={t('products.loadFailedBody')}
        />
      </div>
    );
  }

  const launchProduct = (product: Product) => {
    if (product.code === 'CAMPUSONE_ASSIST') {
      openAssist();
      return;
    }
    if (!product.launchUrl) return;
    window.open(product.launchUrl, '_blank', 'noopener,noreferrer');
  };

  const canLaunch = (product: Product) => {
    if (product.status !== 'AVAILABLE') return false;
    if (product.code === 'CAMPUSONE_ASSIST') return assistAvailable;
    return Boolean(product.launchUrl);
  };

  return (
    <div className="page-products">
      <div className="page-header">
        <h1>{t('products.title')}</h1>
        <p className="page-subtitle">{t('products.subtitle')}</p>
      </div>

      {entitled.length === 0 ? (
        <EmptyState icon={<Icon name="noProducts" size={48} />} title={t('products.noProducts')} description={t('products.noProductsDesc')} />
      ) : (
        <div className="products-grid" role="list" aria-label={t('products.title')}>
          {entitled.map((product) => (
            <Card key={product.code} className="product-card" role="listitem">
              <div className="product-cardArtwork" aria-hidden="true">
                <ProductArtwork code={product.code} />
                <Badge
                  className="product-cardStatus"
                  tone={product.status === 'AVAILABLE' ? 'success' : product.status === 'UNAVAILABLE' ? 'danger' : 'warning'}
                >
                  {t(statusLabels[product.status] || product.status)}
                </Badge>
              </div>
              <div className="product-cardBody">
                <h3 className="product-cardName">{product.name}</h3>
                <p className="product-cardDesc">{product.description}</p>
              </div>
              <Button
                className="product-cardLaunch"
                size="sm"
                variant="primary"
                type="button"
                disabled={!canLaunch(product)}
                aria-label={product.status === 'AVAILABLE' ? `${t('products.launch')} ${product.name}` : `${product.name} — ${t(statusLabels[product.status] || product.status)}`}
                onClick={() => launchProduct(product)}
              >
                {t('products.launch')}{' '}
                {product.code === 'CAMPUSONE_ASSIST' ? (
                  <Icon name="sparkles" size={14} aria-hidden="true" />
                ) : (
                  <Icon name="external" size={14} aria-hidden="true" />
                )}
              </Button>
            </Card>
          ))}
        </div>
      )}
      <AssistLauncherHost open={assistOpen} onClose={closeAssist} />
    </div>
  );
}
